import { useEffect, useRef, useState } from "react";
import Swiper from "swiper/bundle";
import "swiper/css/bundle";

export default function QuantumGenesisStorySlider() {
  const swiperRef = useRef<Swiper | null>(null);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);

  const slides = [
    "/video/videoslider1.mp4",
    "/video/videoslider2.mp4",
    "/video/videoslider3.mp4",
    "/video/videoslider4.mp4",
    "/video/videoslider5.mp4",
  ];

  useEffect(() => {
    swiperRef.current = new Swiper(".mySwiper", {
      effect: "coverflow",
      grabCursor: true,
      centeredSlides: true,
      loop: true,
      slidesPerView: 1.6,
      spaceBetween: -40,
      coverflowEffect: {
        rotate: 0,
        stretch: 0,
        depth: 120,
        modifier: 1.8,
        slideShadows: false,
      },
      autoplay: {
        delay: 5000,
        disableOnInteraction: false,
      },
      observer: true,
      observeParents: true,
      watchSlidesProgress: true,
      on: {
        init: (swiper) => {
          playActiveVideo(swiper.realIndex);
          setActiveIndex(swiper.realIndex);
        },
        slideChange: (swiper) => {
          playActiveVideo(swiper.realIndex);
          setActiveIndex(swiper.realIndex);
        },
      },
    });

    return () => {
      if (swiperRef.current) {
        swiperRef.current.destroy();
      }
    };
  }, []);

  const playActiveVideo = (index: number) => {
    videoRefs.current.forEach((video, i) => {
      if (video) {
        if (i === index) {
          // Set autoplay attribute and try to play
          video.autoplay = true;
          const playPromise = video.play();
          if (playPromise !== undefined) {
            playPromise.catch((error: Error) => {
              console.log("Autoplay prevented:", error);
              // Show play button or handle the prevention
            });
          }
        } else {
          video.pause();
          video.currentTime = 0;
        }
      }
    });
  };

  const goToSlide = (index: number) => {
    if (swiperRef.current) {
      swiperRef.current.slideToLoop(index);
    }
  };

  const goPrev = () => {
    if (swiperRef.current) swiperRef.current.slidePrev();
  };

  const goNext = () => {
    if (swiperRef.current) swiperRef.current.slideNext();
  };

  return (
    <div className="w-full flex flex-col items-center py-0 relative">
      {/* Section Title */}
      <h2 className="font-tokyo text-3xl md:text-4xl font-bold text-[#00eff8] mb-6 text-center drop-shadow-md">
        Quantum Genesis Lore
      </h2>

      {/* Swiper */}
      <div className="relative w-full max-w-[700px] px-4">
        <div className="swiper mySwiper">
          <div className="swiper-wrapper">
            {slides.map((url, idx) => (
              <div className="swiper-slide" key={`slide-${idx}`}>
                <div
                  className="p-[3px] rounded-2xl"
                  style={{
                    backgroundColor: "#00eff8",
                    boxShadow: "0 0 20px #00eff8",
                  }}
                >
                  {url.endsWith(".mp4") ? (
                    <video
                      key={url}
                      ref={(el) => (videoRefs.current[idx] = el)}
                      src={url}
                      muted
                      autoPlay
                      playsInline
                      loop
                      className="h-auto max-h-[90vh] w-auto mx-auto rounded-2xl object-contain"
                    />
                  ) : (
                    <img
                      key={url}
                      src={url}
                      alt={`Quantum Scene ${idx + 1}`}
                      className="h-auto max-h-[90vh] w-auto mx-auto rounded-2xl object-contain"
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Arrow Buttons (Desktop only) */}
        <button
          onClick={goPrev}
          className="hidden md:flex items-center justify-center w-10 h-10 rounded-full bg-[#00eff8] text-black shadow-lg absolute top-1/2 -left-4 -translate-y-1/2 z-10 hover:scale-110 transition"
        >
          ◀
        </button>
        <button
          onClick={goNext}
          className="hidden md:flex items-center justify-center w-10 h-10 rounded-full bg-[#00eff8] text-black shadow-lg absolute top-1/2 -right-4 -translate-y-1/2 z-10 hover:scale-110 transition"
        >
          ▶
        </button>
      </div>

      {/* Pagination Dots */}
      <div className="flex gap-3 mt-6">
        {slides.map((_, idx) => (
          <button
            key={`dot-${idx}`}
            onClick={() => goToSlide(idx)}
            className={`w-4 h-4 rounded-full border-2 border-[#00eff8] transition-all duration-300 ${
              idx === activeIndex ? "bg-[#00eff8] shadow-md shadow-[#00eff8]" : "bg-transparent"
            }`}
          ></button>
        ))}
      </div>
    </div>
  );
}
